import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Plyr from "plyr";
import 'plyr/dist/plyr.css';

gsap.registerPlugin(ScrollTrigger);

// Simple debounce helper
function debounce(fn, wait) {
    var timeout;
    wait = wait || 200;
    return function () {
        var ctx = this, args = arguments;
        clearTimeout(timeout);
        timeout = setTimeout(function () {
            fn.apply(ctx, args);
        }, wait);
    };
}

class VideoOnScroll {
    constructor({
        playerSelector = ".plyr-player",
        wrapperSelector = "section",
        startTrigger = "top 75%"
    } = {}) {
        this.playerSelector = playerSelector;
        this.wrapperSelector = wrapperSelector;
        this.startTrigger = startTrigger;
        this.players = new WeakMap();
        this.autoplayGroups = new Map();
        this.triggers = [];

        this.initPlayers();
        this.initAutoplayScrollTriggers();

        window.addEventListener(
            "resize",
            debounce(() => {
                this.initPlayers();
                this.initAutoplayScrollTriggers();
                this.triggers.forEach(t => t.refresh());
            }, 200)
        );

        document.addEventListener("accordionOpened", () => {
            this.initPlayers();
            this.initAutoplayScrollTriggers();
            this.triggers.forEach(t => t.refresh());

            this.autoplayGroups.forEach((players, wrapper) => {
                if (wrapper) {
                    this.playSection(wrapper, true);
                }
            })

        });
        document.addEventListener("accordionClosed", () => {
            // this.autoplayGroups.forEach((players, wrapper) => {
            //     if (wrapper) {
            //         this.playSection(wrapper, false, true);
            //     }
            // })

        });

        document.addEventListener("slideChangeVideoSlider", (e) => {
            const activeIndex = e.detail.active || 0
            this.autoplayGroups.forEach((players, wrapper) => {
                players.forEach(player => {
                    if (player.vimeo_id === activeIndex) {
                        player.restart();
                        player.play();
                    } else {
                        player.pause()
                    }
                })
            });
        });
    }

    initPlayers() {
        const isMobileView = window.innerWidth < 768;
        document.querySelectorAll(this.playerSelector).forEach(el => {
            if (this.players.has(el)) return;

            const mediaBox = el.closest(".media-box");
            const id = mediaBox.getAttribute('data-id')
            if (isMobileView && mediaBox.classList.contains("-mobile-image")) {
                return;
            }

            const player = this.initPlayer(el);
            const wrapper = el.closest(this.wrapperSelector);
            const isAuto = mediaBox.classList.contains("-autoplay");

            if (isAuto && wrapper) {
                if (!this.autoplayGroups.has(wrapper)) {
                    this.autoplayGroups.set(wrapper, []);
                }
                this.autoplayGroups.get(wrapper).push({ ...player, vimeo_id: id || 0 });
            }
        });

        setTimeout(() => {
            this.triggers.forEach(t => t.refresh());
        }, 100)



    }

    initAutoplayScrollTriggers() {
        this.triggers.forEach(t => t.kill());
        this.triggers.length = 0;

        const pct = parseFloat(this.startTrigger.split(" ")[1]) / 100;
        this.autoplayGroups.forEach((players, wrapper) => {
            const trig = ScrollTrigger.create({
                trigger: wrapper,
                start: this.startTrigger,
                onEnter: () => this.playSection(wrapper),
                onEnterBack: () => this.playSection(wrapper),
                onLeave: () => this.pauseSection(wrapper),
                onLeaveBack: () => this.pauseSection(wrapper)
            });
            this.triggers.push(trig);

            const rect = wrapper.getBoundingClientRect();
            if (rect.top <= window.innerHeight * pct && rect.bottom >= 0) {
                this.playSection(wrapper);
            }
        });
    }

    playSection(wrapper, refresh = false, paused = false) {
        var players = this.autoplayGroups.get(wrapper) || [];

        for (var i = 0; i < players.length; i++) {
            (function (player) {
                var retry = 0, max = 3;
                // If the closest .media-box has the -autoplay class, force mute & zero volume
                var mediaBox = player.elements.container.closest('.media-box');
                var isAuto = mediaBox && mediaBox.classList.contains('-autoplay');
                if (isAuto) {
                    player.muted = true;
                    player.volume = 0;
                }
                function attempt() {
                    var p;
                    try {
                        if (paused) {
                            p = player.pause();
                        } else if (refresh) {
                            p = player.restart();
                            p = player.play();
                        } else {
                            p = player.play()
                        }

                    } catch (e) {
                        p = null;
                    }
                    if (p && typeof p.then === "function") {
                        p.catch(function () {
                            if (retry < max) {
                                retry++;
                                setTimeout(attempt, 100);
                            }
                        });
                    }
                }
                attempt();
                player.on('ready', attempt);
            })(players[i]);
        }
    }

    pauseSection(wrapper) {
        var players = this.autoplayGroups.get(wrapper) || [];
        for (var i = 0; i < players.length; i++) {
            players[i].pause();
        }
    }

    initPlayer(el) {
        if (this.players.has(el)) {
            return this.players.get(el);
        }

        const provider = el.dataset.plyrProvider;
        let mediaEl = el;
        const wrapper = el.closest(".media-box")
        const isAuto = wrapper.classList.contains("-autoplay");
        const duration = parseFloat(wrapper.dataset.duration) || null;

        if (provider === "html5") {
            const srcD = el.dataset.src;
            const srcM = el.dataset.mobileSrc;
            const poster = el.dataset.preview || "";
            const isMob = window.innerWidth <= 768;

            const video = document.createElement("video");
            if (isAuto) {
                video.setAttribute("muted", "");
                video.setAttribute("loop", "");
                video.setAttribute("playsinline", "");
            } else {
                video.setAttribute("controls", "");
            }
            if (poster) {
                video.setAttribute("poster", poster);
            }
            video.src = (isMob && srcM) ? srcM : srcD;

            el.innerHTML = "";
            el.appendChild(video);
            mediaEl = video;
        }

        const player = new Plyr(mediaEl, {
            autoplay: false,
            muted: isAuto,
            volume: 0,
            playsinline: isAuto,
            hideControls: false,
            autopause: false,
            loop: { active: isAuto },
            controls: isAuto ? [] : ["play", "progress", "current-time", "mute", "volume", "fullscreen"],
            previewThumbnails: { enabled: false },
            // duration: duration,
            // ratio: '16:9',
            fullscreen: { enabled: !isAuto },
            vimeo: {
                autoplay: false,
                background: isAuto,
                controls: false,
                muted: isAuto,
                loop: isAuto,
                end_time: duration
            },
            clickToPlay: !isAuto
        });

        player.on('ready', () => {
            // console.log(player);
            if (isAuto) {
                player.toggleControls(false);
                player.muted = true;
                player.volume = 0;
            } else {
                player.muted = false;
                player.volume = 0;
                player.increaseVolume(0.50);
            }

            if (duration) {
                player.on('timeupdate', () => {
                    if (player.currentTime >= duration) {
                        if (isAuto) {
                            player.restart();
                        } else {
                            player.stop();
                        }
                    }
                });
            }
        });

        el.plyr = player;
        this.players.set(el, player);
        return player;
    }
}

// Init
document.addEventListener("DOMContentLoaded", function () {
    new VideoOnScroll({
        playerSelector: ".plyr-player",
        wrapperSelector: "section",
        startTrigger: "top 75%"
    });
});

